import React, { useState, useEffect } from "react";

const RadioCheckInput = ({
  options,
  onChange,
  onTextChange,
  initialSelected = [],
  multiSelect = false,
  variant = "circle", // 'circle' | 'square' | 'card'
  icon = null,
}) => {
  const [selectedOptions, setSelectedOptions] = useState(
    Array.isArray(initialSelected)
      ? initialSelected
      : [initialSelected].filter((id) => id !== null && id !== undefined)
  );

  useEffect(() => {
    // Keep local selection in sync if parent changes
    setSelectedOptions(
      Array.isArray(initialSelected)
        ? initialSelected
        : [initialSelected].filter((id) => id !== null && id !== undefined)
    );
  }, [initialSelected]);

  const handleOptionChange = (optionId) => {
    let newSelected;

    if (multiSelect) {
      // Checkbox-like (multiple)
      newSelected = selectedOptions.includes(optionId)
        ? selectedOptions.filter((id) => id !== optionId)
        : [...selectedOptions, optionId];
    } else {
      // Radio-like (single)
      newSelected = [optionId];
    }

    setSelectedOptions(newSelected);
    onChange?.(multiSelect ? newSelected : optionId);
  };

  const isOptionSelected = (optionId) => selectedOptions.includes(optionId);

  const renderSelectionIndicator = (option, isSelected) => {
    if (variant === "square") {
      return (
        <div
          className={`w-6 h-6 flex items-center justify-center rounded border transition-all duration-300 ${isSelected ? "border-accent bg-accent" : "border-gray-150 bg-white"
            }`}
        >
          {isSelected && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white transition-all duration-300"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      );
    }

    if (variant === "card") {
      return (
        <div
          className={`w-[48px] h-[48px] flex items-center justify-center rounded-full transition-all duration-300 aspect-square ${isSelected ? "bg-accent" : "bg-gray-100"
            }`}
        >
          {option.icon(isSelected ? "white" : "black")} 
        </div>
      );
    }

    // Default circle radio with transitions
    return (
      <div
        className={`w-6 h-6 p-[4px] flex items-center justify-center rounded-full border-2 transition-all duration-300 ${isSelected ? "border-accent" : "border-gray-150"
          }`}
      >
        <div
          className={`w-full h-full aspect-square rounded-full bg-accent transition-all duration-300 ${isSelected ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
        ></div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3 w-full transition-all duration-300">
      {options.map((option) => {
        const isSelected = isOptionSelected(option.id);

        return (
          <div
            key={option.id}
            className={`w-full relative flex items-top px-6 py-5 rounded-xl border-2 cursor-pointer transition-all duration-300 ${isSelected
              ? "border-accent bg-accent-10010"
              : "border-gray-100 bg-white"
              }`}
            onClick={() => handleOptionChange(option.id)}
          >
            {renderSelectionIndicator(option, isSelected)}

            <div className="ml-3 flex flex-col w-full">
              <span className="text-base font-medium transition-all duration-300">
                {option.label}
              </span>

              {option.description && (
                <p className="text-sm text-gray-600 mt-1 transition-all duration-300 line-clamp-2 md:line-clamp-none">
                  {option.description}
                </p>
              )}

              {option.hasTextInput && isSelected && (
                <div className="flex flex-col gap-2 w-full transition-all duration-300">
                  <div className="w-full border-[1px] border-accent-10010 my-2 transition-all duration-300" />
                  <input
                    type="text"
                    placeholder={option.placeholder || ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      onTextChange?.(option.id, e.target.value);
                    }}
                    className="w-full bg-transparent border-none focus:outline-none focus-visible:outline-none placeholder-accent-darker transition-all duration-300"
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RadioCheckInput;
